import {
  collection,
  addDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  updateDoc,
  doc,
  getDocs,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/config/firebase.config';
import { ChatMessage, CreateMessageData } from '@/types';
import { COLLECTIONS } from '@/constants';
import { convertFirestoreDoc } from '@/utils';

/**
 * Send a message in an exchange chat
 */
export const sendMessage = async (data: CreateMessageData): Promise<string> => {
  try {
    const messageData = {
      ...data,
      isRead: false,
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.MESSAGES), messageData);
    return docRef.id;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Get messages for an exchange
 */
export const getMessages = async (exchangeId: string): Promise<ChatMessage[]> => {
  try {
    const messagesRef = collection(db, COLLECTIONS.MESSAGES);
    const q = query(
      messagesRef,
      where('exchangeId', '==', exchangeId),
      orderBy('createdAt', 'asc')
    );

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<ChatMessage>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting messages:', error);
    throw error;
  }
};

/**
 * Subscribe to real-time messages for an exchange
 */
export const subscribeToMessages = (
  exchangeId: string,
  callback: (messages: ChatMessage[]) => void
): Unsubscribe => {
  const messagesRef = collection(db, COLLECTIONS.MESSAGES);
  const q = query(messagesRef, where('exchangeId', '==', exchangeId), orderBy('createdAt', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const messages = snapshot.docs.map((doc) =>
      convertFirestoreDoc<ChatMessage>({
        id: doc.id,
        ...doc.data(),
      })
    );
    callback(messages);
  });
};

/**
 * Mark message as read
 */
export const markMessageAsRead = async (messageId: string): Promise<void> => {
  try {
    const messageRef = doc(db, COLLECTIONS.MESSAGES, messageId);
    await updateDoc(messageRef, {
      isRead: true,
    });
  } catch (error) {
    console.error('Error marking message as read:', error);
    throw error;
  }
};

/**
 * Mark all messages in an exchange as read for a user
 */
export const markExchangeMessagesAsRead = async (
  exchangeId: string,
  userId: string
): Promise<void> => {
  try {
    const messagesRef = collection(db, COLLECTIONS.MESSAGES);
    const q = query(
      messagesRef,
      where('exchangeId', '==', exchangeId),
      where('senderId', '!=', userId),
      where('isRead', '==', false)
    );

    const querySnapshot = await getDocs(q);

    const updatePromises = querySnapshot.docs.map((doc) =>
      updateDoc(doc.ref, { isRead: true })
    );

    await Promise.all(updatePromises);
  } catch (error) {
    console.error('Error marking exchange messages as read:', error);
    throw error;
  }
};

/**
 * Get unread message count for a user
 */
export const getUnreadMessageCount = async (userId: string): Promise<number> => {
  try {
    const messagesRef = collection(db, COLLECTIONS.MESSAGES);
    const q = query(
      messagesRef,
      where('senderId', '!=', userId),
      where('isRead', '==', false)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  } catch (error) {
    console.error('Error getting unread message count:', error);
    return 0;
  }
};
